import http from '@/utils/httpClient';
import { toaster } from '@/utils/toast';
import { Action, Thunk, action, thunk } from 'easy-peasy';

export interface jobPost {
  job_title: string;
  service_type: string;
  start_date: string;
  budget: number;
  job_skills: string[];
  description: string;
  job_type: string;
  work_type: string;
  experience: string;
  project_length: string;
  status: string;
}

export interface jobSearchParams {
  workType: string;
  jobType: string;
  minBudget: number;
  maxBudget: number;
  experience: string;
  search: string;
  jobSkills: string;
}

export interface jobsResponse {
  advisorId: string;
  applicants: number;
  budget: number;
  dateAdded: Date;
  description: string;
  experience: string;
  id: string;
  jobSkills: string[];
  jobTitle: string;
  jobType: string;
  posted: string;
  workType: string;
}

export interface jobDetailsAdvisor {
  id: string;
  advisor_id: string;
  user_id: string;
  job_title: string;
  service_type: string;
  job_skills: string[];
  start_date: string;
  budget: number;
  experience: string;
  status: string;
  job_description: string;
  applicants: number;
  job_type: string;
  work_type: string;
  project_length: string;
  date_added: Date;
  posted: string;
  first_name: string;
  last_name: string;
  advisor_status: string;
  advisor_location: string;
  member_since: Date;
  profilePic: profilePic;
}

export interface jobPostAdvisor {
  jobTitle: string;
  status: string;
  id: string;
  applicants: number;
  jobType: string;
  workType: string;
  date_format: string;
}

export interface jobPosts {
  count: number;
  rows: jobPostAdvisor[];
}

export interface job {
  name: string;
  activeJobs: number;
  jobPosts: jobPosts;
}

export interface advisorJobs {
  message: string;
  job: job;
}

export interface manageJobParams {
  jobId: string;
  flag: 'expire' | 'delete';
}

export interface applyJobDescription {
  skills: string[];
  cover_letter: string;
  job_id: string;
}

export interface applySignature {
  signature: File | undefined;
}

export interface applyJobPayload {
  job_id: string;
  skills: string[];
  cover_letter: string;
  // signature: File
}

export interface profilePic {
  type: string;
  data: [];
}

export interface jobApplication {
  providerId: string;
  status: string;
  provider: {
    experience: string;
    role: string;
    profilePic: profilePic;
    cv: string;
    linkedInUrl: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
    };
  };
}

export interface applciantDescriptionPayload {
  jobId: string;
  applicantId: string;
}

export interface workExperience {
  id: string;
  providerId: string;
  jobTitle: string;
  startDate: string;
  endDate: string;
  company: string;
  workType: string;
  jobType: string;
  description: string;
}

export interface education {
  id: string;
  providerId: string;
  degree: string;
  startDate: string;
  endDate: string;
  school: string;
}

export interface applicantDescription {
  job_id: string;
  provider_id: string;
  name: string;
  linkedin_url: string;
  about: string;
  profilePic: profilePic;
  resume: string;
  cover_letter: string;
  role: string;
  skills: string[];
  application_status: string;
  work_experience: workExperience[];
  education: education[];
}

export interface jobQuerySearchParams {
  jobType: string;
  workType: string;
  experience: string;
  minBudget: string;
  maxBudget: string;
  search: string;
  jobSkills: string;
}

export interface IJobPost {
  jobPost: jobPost | undefined;
  allJobs: jobsResponse[] | undefined;
  myJobs: advisorJobs | undefined;
  recentJobs: advisorJobs | undefined;
  jobDetailsAdvisor: jobDetailsAdvisor | undefined;
  applyJobDescription: applyJobDescription;
  applySignature: applySignature;
  jobApplications: jobApplication[];
  applicantDescription: applicantDescription | undefined;

  setAllJobs: Action<IJobPost, jobsResponse[] | undefined>;
  setMyJobs: Action<IJobPost, advisorJobs>;
  setRecentJobs: Action<IJobPost, advisorJobs>;
  setJobDetailsAdvisor: Action<IJobPost, jobDetailsAdvisor | undefined>;
  setApplyJobDescription: Action<IJobPost, applyJobDescription>;
  setApplySignature: Action<IJobPost, applySignature>;
  setJobApplications: Action<IJobPost, jobApplication[]>;
  setApplicantDescription: Action<IJobPost, applicantDescription>;

  createJobPost: Thunk<IJobPost, jobPost>;
  getJob: Thunk<IJobPost, string>;
  getAllJobs: Thunk<IJobPost, string>;
  getMyJobs: Thunk<IJobPost>;
  getRecentJobs: Thunk<IJobPost, number>;
  getJobDetailsAdvisor: Thunk<IJobPost, string>;
  manageJob: Thunk<IJobPost, manageJobParams>;
  applyJob: Thunk<IJobPost, applyJobPayload>;
  getJobApplications: Thunk<IJobPost, string>;
  getApplicantDescription: Thunk<IJobPost, applciantDescriptionPayload>;
}

const JobPostModel: IJobPost = {
  jobPost: undefined,
  allJobs: undefined,
  myJobs: undefined,
  recentJobs: undefined,
  jobDetailsAdvisor: undefined,
  applyJobDescription: { skills: [], cover_letter: '', job_id: '' },
  applySignature: { signature: undefined },
  jobApplications: [],
  applicantDescription: undefined,

  /*----------- ACTION TYPE ------------*/

  setAllJobs: action((action, payload) => {
    action.allJobs = payload;
  }),

  setMyJobs: action((action, payload) => {
    action.myJobs = payload;
  }),

  setRecentJobs: action((action, payload) => {
    action.recentJobs = payload;
  }),

  setJobDetailsAdvisor: action((action, payload) => {
    action.jobDetailsAdvisor = payload;
  }),

  setApplyJobDescription: action((state, value) => {
    state.applyJobDescription = value;
  }),

  setApplySignature: action((state, value) => {
    state.applySignature = value;
  }),

  setJobApplications: action((state, value) => {
    state.jobApplications = value;
  }),

  setApplicantDescription: action((state, value) => {
    state.applicantDescription = value;
  }),

  /*----------- THUNK TYPE -----------*/
  createJobPost: thunk(async (_action, payload) => {
    // Return the promise so callers can await the outcome and only navigate
    // on success. Optional-chain e?.response?.status: on a network
    // error/timeout there is no `.response`, and the bare `e.response.status`
    // threw inside the catch (unhandled rejection, no toast).
    return http()
      .post('/job', payload)
      .then(() => {
        toaster('success', 'Job post created successfully');
        return true;
      })
      .catch((e) => {
        e?.response?.status === 403
          ? toaster('error', 'Only verified advisors can create job!')
          : toaster('error', 'Error while creating job post!');
        return false;
      });
  }),

  applyJob: thunk(async (_action, payload) => {
    try {
      await http().post('/apply', payload);
      toaster('success', 'Applied successfully');
      return true;
    } catch (e: any) {
      // Optional-chain through the response: pre-fix this was
      // `e.response.data.error` which throws on network errors / timeouts
      // (no .response present), so the catch toast never fired -- the
      // user saw the request hang silently.
      if (e?.response?.data?.error === 'Validation error') {
        toaster('info', 'Already Applied');
      } else {
        toaster('error', 'Error while applying!');
      }
      return false;
    }
  }),

  getJob: thunk(async (_action, payload) => {
    await http()
      .get(`/job/${payload}`)
      .then((res) => {})
      .catch(() => toaster('error', 'error occured in creating job'));
  }),

  getAllJobs: thunk(async (action, payload) => {
    await http()
      .get(`/job?${payload}`)
      .then((res) => {
        action.setAllJobs(res.data?.jobs);
      })
      .catch(() => {
        toaster('error', 'error in fetching job data');
      });
  }),

  getMyJobs: thunk(async (action) => {
    await http()
      .get('/job/list')
      .then((res) => {
        action.setMyJobs(res.data);
      })
      .catch(() => {
        toaster('error', 'error fetching my jobs');
      });
  }),

  getRecentJobs: thunk(async (action, payload) => {
    await http()
      .get(`/job/list?size=${payload}`)
      .then((res) => {
        action.setRecentJobs(res.data);
      })
      .catch(() => {
        toaster('error', 'error fetching my jobs');
      });
  }),

  getJobDetailsAdvisor: thunk(async (action, payload) => {
    await http()
      .get(`/job/${payload}`)
      .then((res) => {
        action.setJobDetailsAdvisor(res.data.job);
      })
      .catch(() => {
        toaster('error', 'error fetching job details');
      });
  }),

  getJobApplications: thunk(async (action, payload) => {
    await http()
      .get(`/apply/applicants/${payload}`)
      .then((res) => {
        action.setJobApplications(res.data);
      })
      .catch(() => {
        toaster('error', 'Error in fetching job applications');
      });
  }),

  manageJob: thunk(async (_action, payload) => {
    // Return the promise so callers can await before reloading the table —
    // a fire-and-forget reload races the mutation and shows stale rows.
    return http()
      .put(`/job/manage?jobId=${payload.jobId}&flag=${payload.flag}`)
      .then(() => {
        toaster('success', 'Updated status successfully');
        return 'success';
      })
      .catch(() => {
        toaster('error', 'Error while updating job status');
        return 'error';
      });
  }),

  getApplicantDescription: thunk(async (action, payload) => {
    await http()
      .get(`/apply/applicant?user_id=${payload.applicantId}&job_post_id=${payload.jobId}`)
      .then((res) => {
        action.setApplicantDescription(res.data.response);
      })
      .catch(() => toaster('error', 'Error in fetching Applicant details'));
  }),
};

export default JobPostModel;
